import { useState, useEffect } from 'react';
import { logout, getCurrentUser } from '../services/auth';
import { useNavigate } from 'react-router-dom';
import { getVersionString } from '../config/version';
import OverviewTab from './tabs/OverviewTab';
import UsersTab from './tabs/UsersTab';
import RevenueTab from './tabs/RevenueTab';
import SubscriptionsTab from './tabs/SubscriptionsTab';
import MobilePlatformTab from './tabs/MobilePlatformTab';
import UserEngagementTab from './tabs/UserEngagementTab';
import ContentTab from './tabs/ContentTab';
import LearningPreferencesTab from './tabs/LearningPreferencesTab';
import SystemHealthTab from './tabs/SystemHealthTab';
import SecurityTab from './tabs/SecurityTab';
import AdoptionTab from './tabs/AdoptionTab';
import CostsTab from './tabs/CostsTab';
import InsightsTab from './tabs/InsightsTab';
import FunnelsTab from './tabs/FunnelsTab';
import GamificationTab from './tabs/GamificationTab';
import RetentionTab from './tabs/RetentionTab';
import SessionActivityTab from './tabs/SessionActivityTab';
import RevenueDeepDiveTab from './tabs/RevenueDeepDiveTab';
import OnboardingTab from './tabs/OnboardingTab';
import LearningProgressTab from './tabs/LearningProgressTab';
import FeatureUsageTab from './tabs/FeatureUsageTab';
import EmailAnalyticsTab from './tabs/EmailAnalyticsTab';
import SegmentationTab from './tabs/SegmentationTab';
import PerformanceTab from './tabs/PerformanceTab';
import TrendsTab from './tabs/TrendsTab';
import ExperimentationTab from './tabs/ExperimentationTab';
import ContentPerformanceTab from './tabs/ContentPerformanceTab';
import GeographicTab from './tabs/GeographicTab';
import NotificationsTab from './tabs/NotificationsTab';
import SupportIssuesTab from './tabs/SupportIssuesTab';

type Tab = 
  | 'overview'
  | 'users'
  | 'revenue'
  | 'subscriptions'
  | 'mobile-platform'
  | 'content'
  | 'user-engagement'
  | 'learning-preferences'
  | 'system-health'
  | 'security'
  | 'adoption'
  | 'costs'
  | 'insights'
  | 'funnels'
  | 'gamification'
  | 'retention'
  | 'session-activity'
  | 'revenue-deep-dive'
  | 'onboarding'
  | 'learning-progress'
  | 'feature-usage'
  | 'email-analytics'
  | 'segmentation'
  | 'performance'
  | 'trends'
  | 'experimentation'
  | 'content-performance'
  | 'geographic'
  | 'notifications'
  | 'support-issues';

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [user, setUser] = useState<any>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setUser(currentUser);
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const tabs: Array<{ id: Tab; label: string; icon: string }> = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'revenue', label: 'Revenue', icon: '💰' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '📱' },
    { id: 'mobile-platform', label: 'Mobile Platforms', icon: '📲' },
    { id: 'content', label: 'Content', icon: '📝' },
    { id: 'user-engagement', label: 'User Engagement', icon: '📈' },
    { id: 'learning-preferences', label: 'Learning Prefs', icon: '🎓' },
    { id: 'system-health', label: 'System Health', icon: '⚡' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'adoption', label: 'Adoption', icon: '🌱' },
    { id: 'costs', label: 'Costs', icon: '💸' },
    { id: 'insights', label: 'Insights', icon: '💡' },
    { id: 'funnels', label: 'Funnels', icon: '🔄' },
    { id: 'gamification', label: 'Gamification', icon: '🏆' },
    { id: 'retention', label: 'Retention', icon: '📈' },
    { id: 'session-activity', label: 'Sessions', icon: '📱' },
    { id: 'revenue-deep-dive', label: 'Revenue Deep', icon: '💎' },
    { id: 'onboarding', label: 'Onboarding', icon: '🔄' },
    { id: 'learning-progress', label: 'Learning Progress', icon: '🎓' },
    { id: 'feature-usage', label: 'Features', icon: '🎯' },
    { id: 'email-analytics', label: 'Email', icon: '📧' },
    { id: 'segmentation', label: 'Segmentation', icon: '👥' },
    { id: 'performance', label: 'Performance', icon: '⚡' },
    { id: 'trends', label: 'Trends', icon: '📈' },
    { id: 'experimentation', label: 'A/B Tests', icon: '🧪' },
    { id: 'content-performance', label: 'Content Perf', icon: '📊' },
    { id: 'geographic', label: 'Geographic', icon: '🌍' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'support-issues', label: 'Support Issues', icon: '🆘' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab />;
      case 'users':
        return <UsersTab />;
      case 'revenue':
        return <RevenueTab />;
      case 'subscriptions':
        return <SubscriptionsTab />;
      case 'mobile-platform':
        return <MobilePlatformTab />;
      case 'content':
        return <ContentTab />;
      case 'user-engagement':
        return <UserEngagementTab />;
      case 'learning-preferences':
        return <LearningPreferencesTab />;
      case 'system-health':
        return <SystemHealthTab />;
      case 'security':
        return <SecurityTab />;
      case 'adoption':
        return <AdoptionTab />;
      case 'costs':
        return <CostsTab />;
      case 'insights':
        return <InsightsTab />;
      case 'funnels':
        return <FunnelsTab />;
      case 'gamification':
        return <GamificationTab />;
      case 'retention':
        return <RetentionTab />;
      case 'session-activity':
        return <SessionActivityTab />;
      case 'revenue-deep-dive':
        return <RevenueDeepDiveTab />;
      case 'onboarding':
        return <OnboardingTab />;
      case 'learning-progress':
        return <LearningProgressTab />;
      case 'feature-usage':
        return <FeatureUsageTab />;
      case 'email-analytics':
        return <EmailAnalyticsTab />;
      case 'segmentation':
        return <SegmentationTab />;
      case 'performance':
        return <PerformanceTab />;
      case 'trends':
        return <TrendsTab />;
      case 'experimentation':
        return <ExperimentationTab />;
      case 'content-performance':
        return <ContentPerformanceTab />;
      case 'geographic':
        return <GeographicTab />;
      case 'notifications':
        return <NotificationsTab />;
      case 'support-issues':
        return <SupportIssuesTab />;
      default:
        return <OverviewTab />;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative' }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#2563eb',
        color: 'white',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              display: isMobile ? 'block' : 'none',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem',
            }}
            aria-label="Toggle menu"
          >
            ☰
          </button>
          <h1 style={{ margin: 0, fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
            {isMobile ? 'Admin' : 'Mind Tiles Admin Dashboard'}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {!isMobile && <span style={{ fontSize: '0.9rem' }}>{user?.email}</span>}
          {!isMobile && (
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>
              {getVersionString()}
            </span>
          )}
          <button
            onClick={handleLogout}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: isMobile ? '0.875rem' : '1rem',
            }}
          >
            Logout
          </button>
        </div>
      </header>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Mobile Overlay */}
        {sidebarOpen && isMobile && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 999,
            }}
          />
        )}

        {/* Sidebar */}
        <aside style={{
          width: isMobile ? (sidebarOpen ? '250px' : '0') : '250px',
          backgroundColor: '#f8f9fa',
          borderRight: '1px solid #e0e0e0',
          overflowY: 'auto',
          transition: 'width 0.3s ease, left 0.3s ease',
          position: isMobile ? 'fixed' : 'relative',
          left: isMobile ? (sidebarOpen ? 0 : '-250px') : 0,
          top: isMobile ? '60px' : 0,
          height: isMobile ? 'calc(100vh - 60px)' : '100%',
          zIndex: 1000,
        }}>
          <nav style={{ padding: '1rem 0' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (isMobile) {
                    setSidebarOpen(false);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  textAlign: 'left',
                  backgroundColor: activeTab === tab.id ? '#e3f2fd' : 'transparent',
                  border: 'none',
                  borderLeft: activeTab === tab.id ? '3px solid #2563eb' : '3px solid transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: isMobile ? '0.875rem' : '0.95rem',
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: '#ffffff',
          width: '100%',
        }}>
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;

